import * as React from "react"

import { Hash, Link2, Search } from "lucide-react"

import { Input } from "../Input/Input"
import { Loader } from "../Loader/Loader"

export interface ResourceMenuAction {
  label: string
  onSelect: () => void
  icon?: React.ReactNode
  destructive?: boolean
  disabled?: boolean
  loading?: boolean
  /** Keeps the containing menu open after this action is selected. */
  keepOpen?: boolean
  /** Starts a new visual section before this action. */
  separatorBefore?: boolean
  /** Nested actions rendered in a submenu. */
  items?: ResourceMenuAction[]
  /** Adds a search field that filters nested actions by label. */
  searchPlaceholder?: string
  "data-testid"?: string
}

export interface ResourceMenuCopyTargets {
  id?: string
  url?: string
}

type ResourceMenuItemComponent = React.ComponentType<{
  children: React.ReactNode
  className?: string
  destructive?: boolean
  disabled?: boolean
  onSelect?: (event: Event) => void
  "data-testid"?: string
}>

interface ResourceMenuSubmenuComponents {
  root: React.ComponentType<{ children: React.ReactNode }>
  trigger: React.ComponentType<{
    children: React.ReactNode
    className?: string
    disabled?: boolean
  }>
  content: React.ComponentType<{
    children: React.ReactNode
    className?: string
  }>
}

export interface ResourceMenuItemsProps {
  actions: ResourceMenuAction[]
  copy: ResourceMenuCopyTargets
  onCopy: (value: string) => void
  item: ResourceMenuItemComponent
  separator: React.ComponentType
  submenu?: ResourceMenuSubmenuComponents
}

function ResourceMenuSubmenu({
  action,
  renderActions,
  submenu,
}: {
  action: ResourceMenuAction
  renderActions: (actions: ResourceMenuAction[]) => React.ReactNode
  submenu: ResourceMenuSubmenuComponents
}) {
  const [query, setQuery] = React.useState("")
  const normalizedQuery = query.trim().toLocaleLowerCase()
  const visibleItems =
    normalizedQuery === ""
      ? (action.items ?? [])
      : (action.items ?? []).filter((item) =>
          item.label.toLocaleLowerCase().includes(normalizedQuery),
        )
  const SubmenuRoot = submenu.root
  const SubmenuTrigger = submenu.trigger
  const SubmenuContent = submenu.content
  const isSearchable = action.searchPlaceholder !== undefined

  return (
    <SubmenuRoot>
      <SubmenuTrigger
        disabled={action.disabled || action.loading}
        className="gap-2 [&_svg]:size-4"
      >
        {action.loading ? (
          <Loader size="sm" className="size-4 text-current" />
        ) : (
          action.icon
        )}
        {action.label}
      </SubmenuTrigger>
      <SubmenuContent
        className={
          isSearchable ? "w-64 overflow-hidden p-0" : "max-h-72 overflow-y-auto"
        }
      >
        {isSearchable ? (
          <>
            <div
              role="presentation"
              className="border-b border-coval-border p-2"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <Input
                type="search"
                startAdornment={<Search />}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={action.searchPlaceholder}
                aria-label={action.searchPlaceholder}
                className="h-8"
              />
            </div>
            <div className="max-h-72 overflow-y-auto overflow-x-hidden p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {visibleItems.length > 0 ? (
                renderActions(visibleItems)
              ) : (
                <p className="px-2 py-4 text-center text-sm text-coval-text-muted">
                  No matching items
                </p>
              )}
            </div>
          </>
        ) : (
          renderActions(visibleItems)
        )}
      </SubmenuContent>
    </SubmenuRoot>
  )
}

function splitAtRequestedSeparators(
  actions: ResourceMenuAction[],
): ResourceMenuAction[][] {
  const sections: ResourceMenuAction[][] = []
  for (const action of actions) {
    if (action.separatorBefore || sections.length === 0) {
      sections.push([])
    }
    sections.at(-1)?.push(action)
  }
  return sections
}

export function ResourceMenuItems({
  actions,
  copy,
  onCopy,
  item: Item,
  separator: Separator,
  submenu,
}: ResourceMenuItemsProps) {
  const standardActions = actions.filter((action) => !action.destructive)
  const destructiveActions = actions.filter((action) => action.destructive)
  const copyActions: ResourceMenuAction[] = []
  if (copy.id) {
    const id = copy.id
    copyActions.push({
      label: "Copy ID",
      icon: <Hash />,
      onSelect: () => onCopy(id),
      "data-testid": "resource-copy-id",
    })
  }
  if (copy.url) {
    const url = copy.url
    copyActions.push({
      label: "Copy URL",
      icon: <Link2 />,
      onSelect: () => onCopy(url),
      "data-testid": "resource-copy-url",
    })
  }
  const sections = [
    ...splitAtRequestedSeparators(standardActions),
    copyActions,
    destructiveActions,
  ].filter((section) => section.length > 0)

  const renderActions = (section: ResourceMenuAction[]) =>
    section.map((action) => {
      if (action.items && submenu) {
        return (
          <ResourceMenuSubmenu
            key={action.label}
            action={action}
            renderActions={renderActions}
            submenu={submenu}
          />
        )
      }

      return (
        <Item
          key={action.label}
          destructive={action.destructive}
          disabled={action.disabled || action.loading}
          onSelect={(event) => {
            if (action.keepOpen) {
              event.preventDefault()
            }
            action.onSelect()
          }}
          data-testid={action["data-testid"]}
          className="gap-2 [&_svg]:size-4"
        >
          {action.loading ? (
            <Loader size="sm" className="size-4 text-current" />
          ) : (
            action.icon
          )}
          {action.label}
        </Item>
      )
    })

  return sections.map((section, sectionIndex) => (
    <React.Fragment key={sectionIndex}>
      {sectionIndex > 0 && <Separator />}
      {renderActions(section)}
    </React.Fragment>
  ))
}

export function normalizeResourceCopyTargets({
  id,
  url,
}: {
  id?: string | null
  url?: string | null
}): ResourceMenuCopyTargets {
  const canonicalPath = url?.split(/[?#]/, 1)[0]

  return {
    ...(id ? { id } : {}),
    ...(canonicalPath
      ? {
          url:
            typeof window === "undefined"
              ? canonicalPath
              : new URL(canonicalPath, window.location.origin).toString(),
        }
      : {}),
  }
}
